import React, { useEffect, useState, useRef } from "react";
import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { motion } from "framer-motion";

export default function GameChat({ gameId, user, otherPlayer }) {
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState("");
  const [open, setOpen] = useState(true);
  const endRef = useRef();

  // Fetch chat messages in real-time
  useEffect(() => {
    const q = query(
      collection(db, "games", gameId, "messages"),
      orderBy("timestamp")
    );
    const unsub = onSnapshot(q, (snap) => {
      setMessages(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      setTimeout(
        () => endRef.current?.scrollIntoView({ behavior: "smooth" }),
        50
      );
    });
    return () => unsub();
  }, [gameId]);

  // Send message
  const sendMsg = async () => {
    if (!msg.trim()) return;
    await addDoc(collection(db, "games", gameId, "messages"), {
      text: msg,
      senderId: user.uid,
      senderName: user.displayName,
      senderAvatar: user.photoURL || "",
      timestamp: serverTimestamp(),
    });
    setMsg("");
  };

  // Enter to send
  const onKeyDown = (e) => {
    if (e.key === "Enter") sendMsg();
  };

  return (
    <motion.div
      initial={{ x: 70, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      className={`
        fixed md:absolute bottom-5 right-5 md:bottom-8 md:right-8 z-50
        ${open ? "" : "translate-y-80 md:translate-y-0"}
      `}
      style={{ width: 270, maxWidth: "94vw" }}
    >
      <div className="rounded-2xl shadow-2xl bg-slate-900/90 border-2 border-lime-400/70 overflow-hidden flex flex-col">
        <div className="flex items-center justify-between bg-lime-400/20 px-4 py-2">
          <span className="font-bold text-lime-400 text-base">Game Chat</span>
          <button
            onClick={() => setOpen((v) => !v)}
            className="text-lg font-black text-lime-300 px-2"
          >
            {open ? "−" : "💬"}
          </button>
        </div>
        {open && (
          <div className="flex flex-col h-72 md:h-80 bg-slate-950/80 p-2 space-y-1 overflow-y-auto">
            {messages.map((m) => (
              <div
                key={m.id}
                className={`flex items-end gap-2 ${
                  m.senderId === user.uid ? "justify-end" : "justify-start"
                }`}
              >
                {m.senderId !== user.uid && (
                  <img
                    src={m.senderAvatar || "/avatar.png"}
                    alt=""
                    className="w-7 h-7 rounded-full border-2 border-lime-300"
                  />
                )}
                <div
                  className={`px-3 py-2 rounded-2xl text-sm shadow font-semibold 
                  ${
                    m.senderId === user.uid
                      ? "bg-lime-500/80 text-slate-900"
                      : "bg-slate-800/80 text-lime-200"
                  }`}
                >
                  <span>{m.text}</span>
                  <div className="text-xs text-slate-400 mt-0.5">
                    {m.senderName}
                  </div>
                </div>
                {m.senderId === user.uid && (
                  <img
                    src={user.photoURL || "/avatar.png"}
                    alt=""
                    className="w-7 h-7 rounded-full border-2 border-lime-400"
                  />
                )}
              </div>
            ))}
            <div ref={endRef} />
          </div>
        )}
        {open && (
          <div className="flex p-2 gap-2 border-t border-lime-400/30 bg-slate-900/90">
            <input
              type="text"
              className="flex-1 px-3 py-2 rounded-xl bg-slate-800 text-white outline-none"
              placeholder="Say hi…"
              value={msg}
              maxLength={200}
              onChange={(e) => setMsg(e.target.value)}
              onKeyDown={onKeyDown}
            />
            <button
              className="bg-lime-400 text-black font-bold px-4 py-2 rounded-xl shadow hover:bg-lime-300 transition"
              onClick={sendMsg}
              disabled={!msg.trim()}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={22}
                height={22}
                fill="none"
                viewBox="0 0 24 24"
                className="mx-auto"
              >
                <path
                  d="M3 20l18-8-18-8v7l13 1-13 1z"
                  fill="currentColor"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
}
